import { useParams, Link, Navigate } from "react-router-dom";
import { useMemo } from "react";
import { articles, categories } from "@/mocks/data";
import Breadcrumb from "@/components/base/Breadcrumb";

const articleDetailImages: Record<string, string> = {
  "shouji-zipai-ru-men-jiqiao": "https://readdy.ai/api/search-image?query=Elegant%20minimal%20photography%20studio%20setup%20with%20warm%20golden%20amber%20lighting%2C%20clean%20aesthetic%20composition%2C%20soft%20natural%20textures%2C%20modern%20smartphone%20on%20wooden%20surface%2C%20editorial%20quality%2C%20wide%20banner%20format&width=1200&height=600&seq=detail-selfie-basics&orientation=landscape",
  "guochan-zipai-shenqi-tuijian": "https://readdy.ai/api/search-image?query=Modern%20Chinese%20smartphones%20elegantly%20arranged%20on%20a%20minimal%20wooden%20desk%2C%20warm%20golden%20lighting%2C%20clean%20product%20photography%2C%20refined%20editorial%20look%2C%20wide%20banner%20composition&width=1200&height=600&seq=detail-device-guide&orientation=landscape",
  "zipai-goutu-fangfa-san-fen-fa": "https://readdy.ai/api/search-image?query=Artistic%20photography%20composition%20concept%20with%20grid%20lines%20overlay%2C%20warm%20golden%20toned%20abstract%20background%2C%20elegant%20minimalist%2C%20wide%20banner%20format&width=1200&height=600&seq=detail-composition&orientation=landscape",
  "shouji-xiu-tu-app-tui-jian": "https://readdy.ai/api/search-image?query=Elegant%20smartphone%20displaying%20photo%20editing%20interface%2C%20warm%20ambient%20lighting%2C%20minimalist%20editorial%20product%20photography%2C%20wide%20banner%20format%2C%20soft%20golden%20tones&width=1200&height=600&seq=detail-editing-apps&orientation=landscape",
  "zipai-dengguang-buzhi-jiaocheng": "https://readdy.ai/api/search-image?query=Professional%20ring%20light%20studio%20setup%2C%20warm%20golden%20glow%2C%20elegant%20product%20photography%2C%20wide%20banner%20format%2C%20minimal%20studio%20aesthetic&width=1200&height=600&seq=detail-lighting&orientation=landscape",
  "guochan-shouji-meiyan-shezhi": "https://readdy.ai/api/search-image?query=Elegant%20cosmetic%20products%20on%20minimal%20vanity%20desk%2C%20warm%20soft%20lighting%2C%20refined%20editorial%20photography%2C%20wide%20banner%2C%20rose%20and%20golden%20tones&width=1200&height=600&seq=detail-beauty-mode&orientation=landscape",
  "yeshi-zipai-jiqiao": "https://readdy.ai/api/search-image?query=Elegant%20night%20cityscape%20with%20warm%20neon%20lights%2C%20minimalist%20urban%20photography%2C%20soft%20bokeh%2C%20wide%20banner%2C%20refined%20atmosphere&width=1200&height=600&seq=detail-night-selfie&orientation=landscape",
  "jixiang-zu-jia-zipai-weizhi": "https://readdy.ai/api/search-image?query=Beautiful%20Chinese%20landscape%20with%20traditional%20architecture%2C%20warm%20golden%20sunset%2C%20travel%20photography%2C%20wide%20banner%2C%20minimalist%20composition&width=1200&height=600&seq=detail-travel-spots&orientation=landscape",
};

function renderMarkdown(md: string): string {
  let html = md
    .replace(/### (.+)/g, "<h4 class=\"font-heading text-base text-foreground-900 mt-8 mb-3 font-semibold\">$1</h4>")
    .replace(/## (.+)/g, "<h2 class=\"font-heading text-xl md:text-2xl text-foreground-900 mt-10 mb-5\">$1</h2>")
    .replace(/\*\*(.+?)\*\*/g, "<strong class=\"text-foreground-900 font-semibold\">$1</strong>")
    .replace(/\n\n/g, "</p><p class=\"text-foreground-700 leading-relaxed mb-4 text-sm md:text-base\">")
    .replace(/\n- /g, "\n<li class=\"text-foreground-700 text-sm md:text-base ml-4 mb-1 list-disc\">")
    .replace(/\n\d\. /g, "\n<li class=\"text-foreground-700 text-sm md:text-base ml-4 mb-1 list-decimal\">");

  html = "<p class=\"text-foreground-700 leading-relaxed mb-4 text-sm md:text-base\">" + html + "</p>";
  html = html.replace(/<\/p><p[^>]*>\n<li/g, "<ul class=\"mb-4 space-y-1\">\n<li");
  html = html.replace(/<\/li>\n<p[^>]*>/g, "</li>\n</ul><p class=\"text-foreground-700 leading-relaxed mb-4 text-sm md:text-base\">");
  html = html.replace(/<\/li>\n<h[2-4]/g, "</li>\n</ul><h2");

  return html;
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);
  const category = useMemo(
    () => (article ? categories.find((c) => c.slug === article.category) : undefined),
    [article]
  );

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.category === article.category && a.slug !== article.slug)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <main>
        {/* Article Header */}
        <section className="pt-24 pb-0 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
                { label: article.title },
              ]}
            />
          </div>
        </section>

        <article className="pb-14 md:pb-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            {/* Featured Image */}
            <div className="mt-8 rounded-xl overflow-hidden">
              <img
                src={articleDetailImages[article.slug] || ""}
                alt={article.title}
                className="w-full aspect-[2/1] object-cover object-top"
              />
            </div>

            {/* Meta */}
            <div className="mt-8">
              <div className="flex items-center gap-3 flex-wrap mb-4">
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="px-3 py-1 text-xs font-medium rounded-full bg-accent-100/80 text-accent-700 hover:bg-accent-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {category.name}
                  </Link>
                )}
                <span className="text-xs text-foreground-500">{article.readTime}</span>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground-900 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-3 mt-5 text-sm text-foreground-500">
                <span>作者：{article.author}</span>
                <span>·</span>
                <span>发布于 {article.date}</span>
              </div>
            </div>

            {/* Content */}
            <div
              className="mt-10 article-content"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(article.content) }}
            />

            {/* Tags / Bottom */}
            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2">
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="px-3 py-1.5 text-xs rounded-lg bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors cursor-pointer"
                  >
                    # {category.name}
                  </Link>
                )}
                <span className="px-3 py-1.5 text-xs rounded-lg bg-secondary-100 text-secondary-700">
                  # 国产自拍
                </span>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-8">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => {
                  const raCat = categories.find((c) => c.slug === ra.category);
                  return (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="group rounded-xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative h-40 overflow-hidden">
                        <img
                          src={articleDetailImages[ra.slug] || ""}
                          alt={ra.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-3 mb-2">
                          {raCat && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-accent-100/80 text-accent-700 whitespace-nowrap">
                              {raCat.name}
                            </span>
                          )}
                          <span className="text-xs text-foreground-500">{ra.readTime}</span>
                        </div>
                        <h3 className="font-heading text-sm text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {ra.title}
                        </h3>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        )}
      </main>

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.excerpt,
            "author": {
              "@type": "Person",
              "name": article.author,
            },
            "datePublished": article.date,
            "inLanguage": "zh-CN",
            "articleSection": category?.name || "",
            "url": `https://www.ziguo自拍指南.com/article/${article.slug}`,
            "publisher": {
              "@type": "Organization",
              "name": "国产自拍指南",
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "首页",
                "item": "https://www.ziguo自拍指南.com",
              },
              ...(category
                ? [
                    {
                      "@type": "ListItem",
                      "position": 2,
                      "name": category.name,
                      "item": `https://www.ziguo自拍指南.com/category/${category.slug}`,
                    },
                  ]
                : []),
              {
                "@type": "ListItem",
                "position": category ? 3 : 2,
                "name": article.title,
              },
            ],
          }),
        }}
      />
    </>
  );
}